import {
  Box,
  Button,
  Divider,
  Grid,
  makeStyles,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { coverageInfoForProduct } from 'data-layer-cr/dist/functions/coverages';
import {
  ProductCoverageCatalog,
  Quotation,
  QuotationCoverage,
  QuotationUnit,
} from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { H4Text, NetworkIndicator, colors } from 'design-system';
import { useEffect, useMemo, useState } from 'react';
import { Info } from 'react-feather';
import { ConnectedProps, connect } from 'react-redux';

import { Tabs } from 'src/components/General';

// Redux related
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CarItem from 'src/models/helperTypes/CarItem';
import CustomerItem from 'src/models/helperTypes/CustomerItem';
import { PricingPackagesState } from 'src/store/pricingPackages/slice/pricingPackagesSlice';
import { requestQuotationUpdate } from 'src/store/quotation/actions';
import {
  REQUEST_PRICING,
  REQUEST_PRICING_INITIAL,
} from 'src/store/quotation/requestIdentifiers';
import PricingProfileResponse from '../../../../../models/responseTypes/PricingProfileResponse';
import { GET_COVERAGES_FOR_PRODUCT } from '../../../../../store/coverage/requestIdentifiers';
import { RootState } from '../../../../../store/rootReducer';

import { Plan } from '../../components/OptionPriceCard/SelectPlan';
import { IndividualOption } from './IndividualOption';

const useStyles = makeStyles((theme) => ({
  divider: {
    height: 1,
    width: '100vw',
    backgroundColor: '#CEE8FF',
    marginLeft: '-16px',
  },
  includeText: {
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '250px',
    },
  },
  resetButton: {
    display: 'flex',
    fontSize: 16,
    color: colors.bright_turquoise,
    paddingRight: 5,
    paddingLeft: 30,
    paddingTop: 10,
    paddingBottom: 10,
    fontWeight: 700,
    lineHeight: '19px',
    [theme.breakpoints.down('sm')]: {
      width: '200px',
    },
  },
  resetButtonIcon: {
    marginRight: '10px',
    fontSize: '24px',
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
}));

export const IndividualSection = ({
  quotation,
  currentProfile,
  coveragesForProduct,
  productId,
  selectedEndorsement,
  savedQuotationUnit,
  onEndorsementUpdated,
  onChangeCoverageLimit,
  onChangeDeductible,
  onEndorsementUpdatedExemption,
  onCustomerProfileUpdated,
  plans,
  driverData,
  carData,
  reqQuotationUpdate,
}: IndividualSectionProps): JSX.Element => {
  const classes = useStyles();
  const networkStatus = useNetworkObserver(GET_COVERAGES_FOR_PRODUCT);
  const { coverages, coverageResumes } = useMemo(() => {
    return coverageInfoForProduct(coveragesForProduct, productId);
  }, [coveragesForProduct, productId]);
  const requestingQuotationState = useNetworkObserver(REQUEST_PRICING);
  const requestingQuotationInititalState = useNetworkObserver(
    REQUEST_PRICING_INITIAL,
  );

  const [currentPage, updateCurrentPage] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentUnits, setCurrentUnits] = useState(quotation.units);

  useEffect(() => {
    setCurrentUnits(quotation.units);
  }, [quotation.units]);

  const tabs = currentUnits.map((item: QuotationUnit, index: number) => ({
    label: item.package.description,
    index,
  }));

  const [loadingPackage, setLoadingPackage] = useState(-1);

  const onChangeLoadingPackage = (packageOption: number): void => {
    setLoadingPackage(packageOption);
  };

  useEffect(() => {
    if (requestingQuotationState !== 'requesting') {
      setLoadingPackage(-1);
    }
  }, [requestingQuotationState]);

  return (
    <>
      <NetworkIndicator
        data-testid="loading"
        message="Cargando información..."
        requesting={
          networkStatus === 'requesting' ||
          requestingQuotationInititalState === 'requesting'
        }
      />
      {networkStatus !== 'requesting' &&
        requestingQuotationInititalState !== 'requesting' && (
          <Grid
            container
            spacing={2}
            alignItems="flex-start"
            justifyContent="center"
          >
            <Grid
              item
              container
              xs={12}
              data-testid="individual-quotation-text"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  className={classes.includeText}
                >
                  <Info color={colors.madison} />
                  &nbsp;&nbsp;&nbsp;
                  <H4Text style={{ fontWeight: 700, fontSize: 15 }}>
                    La cotización incluirá las 3 opciones.
                  </H4Text>
                </Box>
                <Box>
                  <Button
                    data-testid="sales-web-button-reset-values"
                    className={classes.resetButton}
                    onClick={() =>
                      reqQuotationUpdate(carData, driverData, true)
                    }
                    disabled={requestingQuotationState === 'requesting'}
                  >
                    <FontAwesomeIcon
                      icon={faArrowsRotate}
                      className={classes.resetButtonIcon}
                    />
                    RESTABLECER COBERTURAS
                  </Button>
                </Box>
              </Box>
            </Grid>
            {isMobile && (
              <>
                <Grid
                  item
                  container
                  xs={12}
                  data-testid="individual-section-mobile"
                >
                  <Tabs
                    data-testid="individual-tabs"
                    tabs={tabs}
                    backgroundColor="transparent"
                    currentTab={currentPage}
                    onTabChange={(newTab): void => updateCurrentPage(newTab)}
                    moneyType={currentProfile.typeOfMoney}
                    disabled={requestingQuotationState === 'requesting'}
                  />
                  <Divider className={classes.divider} />
                </Grid>
                <Grid item xs={12}>
                  <IndividualOption
                    packageOption={currentPage}
                    anualPrice={quotation.units[currentPage].total.grandTotal}
                    currentProfile={currentProfile}
                    quotation={quotation}
                    coverages={coverages}
                    unit={quotation.units[currentPage]}
                    coverageResumes={coverageResumes}
                    selectedEndorsement={selectedEndorsement}
                    savedQuotationUnit={savedQuotationUnit}
                    onEndorsementUpdated={onEndorsementUpdated}
                    onEndorsementUpdatedExemption={
                      onEndorsementUpdatedExemption
                    }
                    onCustomerProfileUpdated={onCustomerProfileUpdated}
                    onChangeCoverageLimit={onChangeCoverageLimit}
                    onChangeDeductible={onChangeDeductible}
                    plans={plans}
                    units={quotation.units}
                    loadingPackage={loadingPackage}
                    onChangeLoadingPackage={() =>
                      onChangeLoadingPackage(currentPage)
                    }
                  />
                </Grid>
              </>
            )}

            {!isMobile && (
              <Grid
                container
                spacing={2}
                data-testid="individual-section-desktop"
                alignItems="flex-start"
                justifyContent="center"
              >
                {quotation.units.map((unit, index) => (
                  <Grid key={`OptionPrice-${index + 1}`} item xs={12} md={4}>
                    <IndividualOption
                      packageOption={index}
                      anualPrice={unit.total.grandTotal}
                      currentProfile={currentProfile}
                      quotation={quotation}
                      coverages={coverages}
                      coverageResumes={coverageResumes}
                      unit={unit}
                      selectedEndorsement={selectedEndorsement}
                      savedQuotationUnit={savedQuotationUnit}
                      onEndorsementUpdated={onEndorsementUpdated}
                      onEndorsementUpdatedExemption={
                        onEndorsementUpdatedExemption
                      }
                      onCustomerProfileUpdated={onCustomerProfileUpdated}
                      onChangeCoverageLimit={onChangeCoverageLimit}
                      onChangeDeductible={onChangeDeductible}
                      plans={plans}
                      loadingPackage={loadingPackage}
                      onChangeLoadingPackage={() =>
                        onChangeLoadingPackage(index)
                      }
                      units={quotation.units}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        )}
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  coveragesForProduct: ProductCoverageCatalog;
  currentProfile: PricingProfileResponse;
  savedQuotationUnit: QuotationUnit[];
  pricingPackage: PricingPackagesState;
  plans: Plan[];
  driverData: CustomerItem;
  carData: CarItem;
} => ({
  quotation: state.quotation.currentQuotation,
  coveragesForProduct: state.coverage.coveragesForProduct,
  pricingPackage: state.pricingPackages,
  currentProfile: state.quotation.currentProfile,
  savedQuotationUnit: state.quotation.savedQuotationUnit,
  plans: state.quotation.plans,
  driverData: state.customer.currentCustomer,
  carData: state.car.currentCar,
});

const reduxActions = {
  reqQuotationUpdate: requestQuotationUpdate,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IndividualSectionProps extends PropsFromRedux {
  productId: string;
  selectedEndorsement: QuotationCoverage;
  onEndorsementUpdated: (
    modifiedPackage: number,
    newCode: string,
    newValue: number,
  ) => void;
  onEndorsementUpdatedExemption: (
    modifiedPackage: number,
    addExemptionCoverage: boolean,
    packageSelected: string,
  ) => void;
  onCustomerProfileUpdated: (
    modifiedPackage: number,
    newCode: string,
    newValue: number,
    packageSelected: string,
  ) => void;
  onChangeCoverageLimit: (
    modifiedPackage: number,
    modifiedCoverage: QuotationCoverage[],
    shouldUpdate: boolean,
    packageSelected: string,
  ) => void;
  onChangeDeductible: (
    modifiedPackage: number,
    deductibleOption: number,
  ) => void;
}

export default connector(IndividualSection);
